import { acceptHMRUpdate, defineStore } from 'pinia';

import { ScreeningSensitivity } from '@tallo/property';

import {
  CompanyType,
  InvestorOnboardingSignUpDto,
  InvestorOnboardingSignUpInfoDto,
} from '../dtos/investor-onboarding-sign-up.dto';
import { InvestorOnboardingStep } from '../enums/investor-onboarding-step.enum';
import investorOnboardingService from '../services/investor-onboarding.service';

export enum AccountType {
  Company = 'Company',
  Individual = 'Individual',
}

// Values must match the backend CommunicationChannel enum
// (tallo-be/src/modules/shared/communication/conversation/enums/preferred-communication-channel.enum.ts)
export enum PreferredCommunicationChannel {
  Email = 'Email',
  Sms = 'SMS',
}

interface OnboardingState {
  loaded: boolean;
  completedSteps: InvestorOnboardingStep[];
  /** Joined through an invite: only the personal steps are asked, and companyName is the team's */
  joinedExistingCompany: boolean;
  accountType: CompanyType | null;

  companyName: string;
  numberOfEmployees: string | null; // use enum value string from investor/company options
  numberOfUnitsOverseeing: number | null;
  perceivedProductValueProposition: string;
  screeningSensitivity: ScreeningSensitivity | null;
  preferredCommunicationChannel: PreferredCommunicationChannel | null;
}

/**
 * Mirrors what the server has saved, and only that: a step's answers land here once its save
 * succeeded, so a reload, a closed tab or a second device resumes from the same place. The
 * user's phone lives on the investor store, which the verification modal already keeps fresh.
 */
export const useOnboardingStore = defineStore({
  id: 'investorOnboarding',
  state: (): OnboardingState => ({
    loaded: false,
    completedSteps: [],
    joinedExistingCompany: false,
    accountType: null,
    companyName: '',
    numberOfEmployees: null,
    numberOfUnitsOverseeing: null,
    perceivedProductValueProposition: '',
    screeningSensitivity: null,
    preferredCommunicationChannel: null,
  }),
  actions: {
    async load(): Promise<void> {
      this.hydrate(await investorOnboardingService.getSignUpInfo());
      this.loaded = true;
    },

    /**
     * Only answers from steps the user actually finished come back into the form: a fresh
     * account's company row already carries the owner's own name and a default channel, and
     * neither should show up as if the user had chosen it.
     */
    hydrate(info: InvestorOnboardingSignUpInfoDto): void {
      this.completedSteps = info.completedSteps;
      this.joinedExistingCompany = Boolean(info.joinedExistingCompany);

      // The team's company is already named, so the wizard can greet them with it
      if (this.joinedExistingCompany) {
        this.companyName = info.companyName ?? '';
      }

      if (this.isCompleted(InvestorOnboardingStep.AccountType)) {
        this.accountType = info.accountType;
      }
      if (this.isCompleted(InvestorOnboardingStep.CompanyInfo)) {
        this.companyName = info.companyName ?? '';
        this.numberOfEmployees = info.numberOfEmployees;
        this.numberOfUnitsOverseeing = info.numberOfUnitsOverseeing;
        this.perceivedProductValueProposition = info.perceivedProductValueProposition ?? '';
      }
      if (this.isCompleted(InvestorOnboardingStep.RentalStrategy)) {
        this.screeningSensitivity = info.screeningSensitivity;
      }
      if (this.isCompleted(InvestorOnboardingStep.Communication)) {
        this.preferredCommunicationChannel = info.preferredCommunicationChannel as PreferredCommunicationChannel;
      }
    },

    isCompleted(step: InvestorOnboardingStep): boolean {
      return this.completedSteps.includes(step);
    },

    /** Saves a finished step's answers and marks the step done once the server has them */
    async saveStep(step: InvestorOnboardingStep, answers: Omit<InvestorOnboardingSignUpDto, 'completedStep'>) {
      await investorOnboardingService.saveSignUpInfo({ ...answers, completedStep: step });

      // Fields a step left out arrive as undefined and must not blank what is already known
      const given = Object.fromEntries(Object.entries(answers).filter(([, value]) => value !== undefined));
      this.$patch(given as Partial<OnboardingState>);
      if (!this.isCompleted(step)) {
        this.completedSteps = [...this.completedSteps, step];
      }
    },

    async complete(): Promise<void> {
      await investorOnboardingService.completeInitialOnboarding();
    },

    reset() {
      this.$reset();
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useOnboardingStore, import.meta.hot));
}
